import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";
import { motion } from "framer-motion";
import {
  ArrowLeft, Star, ShieldCheck, Truck, RotateCcw, BadgeCheck, Heart, Share2,
  Minus, Plus, ShoppingBag, Bolt, Package,
} from "lucide-react";
import ProductCard from "@/components/store/ProductCard";
import ReviewsSection from "@/components/store/ReviewsSection";
import { toast } from "sonner";
import { useSeo, SITE_URL, SITE_NAME } from "@/lib/useSeo";
import { isDigitalFormat, effectiveShipTimes, deliveryRangeText } from "@/lib/shipping";

function Stars({ rating = 4.7, count = 0, showCount = true }) {
  const stars = Math.round(rating);
  return (
    <div className="flex items-center gap-2">
      <div className="flex text-accent">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={16} className={i < stars ? "text-accent" : "text-slate-300"} fill="currentColor" />
        ))}
      </div>
      <span className="text-sm font-medium text-slate-900">{rating.toFixed(1)}</span>
      {showCount && <span className="text-sm text-slate-500">({count} reviews)</span>}
    </div>
  );
}

export default function BookDetail() {
  const { id, slug } = useParams();
  const key = slug || id;
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [format, setFormat] = useState("physical");
  const [wishlisted, setWishlisted] = useState(false);
  const { add, setOpen, shippingSettings } = useCart();

  useEffect(() => {
    setNotFound(false);
    setBook(null);
    // Try direct id lookup first; if that fails try slug endpoint
    const looksLikeId = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(key);
    const fetcher = looksLikeId
      ? api.get(`/books/${key}`)
      : api.get(`/catalog/books/by-slug/${key}`);
    fetcher
      .catch(() => api.get(`/catalog/books/by-slug/${key}`))
      .then((r) => {
        if (!r) return;
        setBook(r.data);
        setFormat(r.data.formats?.[0] || "physical");
        setQty(1);
        if (r.data.category) {
          api.get("/books", { params: { category: r.data.category, limit: 8 } })
            .then((rr) => setRelated((rr.data || []).filter((b) => b.id !== r.data.id).slice(0, 5)))
            .catch(() => {});
        }
      }).catch(() => setNotFound(true));
  }, [key]);

  // ---- SEO: title, meta description, canonical, Product JSON-LD ----
  // Called unconditionally BEFORE any early return so hooks-order rules are respected.
  const canonicalPath = book?.slug ? `/product/${book.slug}` : book?.id ? `/book/${book.id}` : null;
  const seoTitle = book ? `${book.title}${book.author ? ` by ${book.author}` : ""}` : null;
  const rawDesc = book?.meta_description || book?.seo_description || book?.description || "";
  const seoDescription = rawDesc ? String(rawDesc).replace(/\s+/g, " ").slice(0, 300) : null;
  const gtin = book ? (
    (book.isbn_13 || book.isbn || book.isbn_10 || "").toString().replace(/[\s-]/g, "") || null
  ) : null;
  const inStockForSeo = (book?.stock ?? 0) > 0;
  const productJsonLd = book ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: book.title,
    description: seoDescription || undefined,
    image: book.cover_image ? [book.cover_image] : undefined,
    sku: book.sku || book.id,
    ...(gtin ? { gtin13: gtin.length === 13 ? gtin : undefined, gtin: gtin } : {}),
    brand: { "@type": "Brand", name: book.publisher || book.author || SITE_NAME },
    ...(book.author ? { author: { "@type": "Person", name: book.author } } : {}),
    ...(book.publisher ? { publisher: { "@type": "Organization", name: book.publisher } } : {}),
    ...(book.isbn || book.isbn_13 || book.isbn_10 ? {
      isbn: book.isbn_13 || book.isbn || book.isbn_10,
    } : {}),
    ...(book.language ? { inLanguage: book.language } : {}),
    offers: {
      "@type": "Offer",
      url: canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined,
      priceCurrency: "USD",
      price: Number(book.price || 0).toFixed(2),
      itemCondition: "https://schema.org/NewCondition",
      availability: inStockForSeo ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      seller: { "@type": "Organization", name: SITE_NAME },
    },
    ...((book.review_count ?? 0) > 0 ? {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(book.rating).toFixed(1),
        reviewCount: book.review_count,
      },
    } : {}),
  } : null;

  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonicalPath,
    image: book?.cover_image,
    jsonLd: productJsonLd,
    ogType: "product",
  });

  if (notFound) {
    return (
      <div className="container-book py-24 text-center" data-testid="book-detail-not-found">
        <p className="text-primary font-semibold text-sm">Not found</p>
        <h1 className="mt-2 font-heading font-bold text-3xl md:text-4xl text-slate-900">We couldn't find that book.</h1>
        <p className="mt-3 text-slate-600">It may have moved or sold out.</p>
        <Link to="/shop" className="inline-block mt-6 px-6 py-3 rounded-xl bg-primary hover:bg-secondary text-white font-semibold">Continue browsing →</Link>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="container-book py-16">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="skeleton aspect-book" />
          <div>
            <div className="skeleton h-6 w-1/3 mb-4" />
            <div className="skeleton h-10 w-full mb-3" />
            <div className="skeleton h-10 w-2/3 mb-8" />
            <div className="skeleton h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const price = Number(book.price || 0);
  const original = book.original_price ? Number(book.original_price) : (book.discount_percent ? price / (1 - book.discount_percent / 100) : null);
  const discount = original && original > price ? Math.round(((original - price) / original) * 100) : 0;
  const stock = book.stock ?? 0;
  const inStock = stock > 0;

  // Delivery estimate: per-book overrides fall back to the global shipping policy
  const shipTimes = effectiveShipTimes(book, shippingSettings);
  const deliveryRange = deliveryRangeText(shipTimes.handling, shipTimes.min, shipTimes.max);
  const digital = isDigitalFormat(format);
  const effShipPrice = book.shipping_price != null ? Number(book.shipping_price) : Number(shippingSettings.base_price || 0);

  const doAdd = (openCart = false) => {
    if (!inStock) return;
    const ok = add(book, format, { quantity: qty, openSheet: openCart });
    if (ok) toast.success(`${book.title} × ${qty} added to cart`);
  };

  const buyNow = () => {
    if (!inStock) return;
    add(book, format, { quantity: qty, openSheet: false });
    navigate("/checkout");
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: book.title, url: window.location.href });
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied");
        return;
      }
      throw new Error("Clipboard unavailable");
    } catch (err) {
      // Fallback: pop a prompt so users can copy manually
      try { window.prompt("Copy this link:", window.location.href); } catch (_) { /* noop */ }
      toast.info("Copy the link above to share.");
    }
  };

  return (
    <div className="container-book py-4 md:py-6" data-testid={`book-detail-${book.id}`}>
      {/* Breadcrumbs */}
      <nav className="text-xs text-slate-500 mb-3" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li>/</li>
          <li><Link to="/shop" className="hover:text-primary">Shop</Link></li>
          {book.category && (<><li>/</li><li><Link to={`/shop?category=${encodeURIComponent(book.category)}`} className="hover:text-primary">{book.category}</Link></li></>)}
          <li>/</li>
          <li className="text-slate-900 font-medium truncate max-w-[200px]">{book.title}</li>
        </ol>
      </nav>

      <div className="grid md:grid-cols-12 gap-6 lg:gap-10">
        {/* Image gallery */}
        <div className="md:col-span-5 lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="sticky top-32 max-w-[340px]"
          >
            <div className="aspect-book bg-white rounded-lg border border-slate-200 shadow-soft overflow-hidden product-image-wrap">
              {book.cover_image ? (
                <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full text-slate-300">No image</div>
              )}
            </div>
            {/* Small thumbs row (single image for now) */}
            <div className="mt-3 flex gap-2">
              {[book.cover_image].filter(Boolean).map((src, i) => (
                <button key={i} className="w-12 h-14 rounded overflow-hidden border-2 border-primary">
                  <img src={src} alt={`Thumb ${i+1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Details */}
        <div className="md:col-span-7 lg:col-span-8">
          {book.category && (
            <Link to={`/shop?category=${encodeURIComponent(book.category)}`} className="inline-block text-[11px] font-semibold uppercase tracking-wider text-primary hover:text-secondary">
              {book.category}
            </Link>
          )}
          <h1 className="mt-1 font-heading font-bold text-[24px] leading-tight text-slate-900" data-testid="book-detail-title">
            {book.title}
          </h1>
          <p className="mt-1 text-sm text-slate-500">by <span className="text-slate-700 font-medium">{book.author}</span></p>

          {(book.review_count ?? 0) > 0 ? (
            <div className="mt-2" data-testid="book-detail-rating">
              <Stars rating={book.rating} count={book.review_count} />
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-400" data-testid="book-detail-no-reviews">No reviews yet</p>
          )}

          {/* Price block */}
          <div className="mt-3 p-4 bg-white rounded-lg border border-slate-200 shadow-soft">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-heading font-bold text-[28px] leading-none text-slate-900" data-testid="book-detail-price">${price.toFixed(2)}</span>
              {original && original > price && (
                <>
                  <span className="text-sm text-slate-400 line-through">${original.toFixed(2)}</span>
                  <span className="badge-discount">Save {discount}%</span>
                </>
              )}
            </div>
            <div className="mt-1.5 flex items-center gap-2 text-[13px] flex-wrap">
              {inStock ? (
                <>
                  {stock <= 5 ? (
                    <span className="badge-lowstock">Only {stock} left in stock — order soon</span>
                  ) : (
                    <span className="badge-instock">In stock · {stock} available</span>
                  )}
                  {digital ? (
                    <span className="text-slate-600" data-testid="book-detail-digital-delivery">
                      Instant digital delivery · No shipping
                    </span>
                  ) : (
                    <>
                      <span className="text-slate-600" data-testid="book-detail-delivery-estimate">Estimated delivery <strong>{deliveryRange}</strong></span>
                      {effShipPrice > 0 ? (
                        <span className="text-slate-600" data-testid="book-detail-shipping">
                          · Shipping <strong>${effShipPrice.toFixed(2)}</strong>
                        </span>
                      ) : (
                        <span className="text-emerald-700 font-medium" data-testid="book-detail-shipping-free">
                          · Free shipping
                        </span>
                      )}
                    </>
                  )}
                </>
              ) : (
                <span className="badge-oos">Out of stock</span>
              )}
            </div>

            {/* Formats */}
            {book.formats && book.formats.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Format</p>
                <div className="flex flex-wrap gap-2">
                  {book.formats.map((f) => (
                    <button
                      key={f}
                      onClick={() => setFormat(f)}
                      data-testid={`format-${f}`}
                      className={`px-3 h-8 rounded-md border text-[13px] font-medium transition-colors ${format === f ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity + CTAs */}
            {inStock ? (
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <div className="inline-flex items-center border border-slate-200 rounded-md h-10">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} className="w-9 h-full flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Decrease quantity" data-testid="qty-decrease"><Minus size={14} /></button>
                  <span className="min-w-[36px] text-center text-sm font-semibold" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(stock, q + 1))} disabled={qty >= stock} className="w-9 h-full flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Increase quantity" data-testid="qty-increase"><Plus size={14} /></button>
                </div>
                <Button
                  onClick={() => doAdd(true)}
                  disabled={qty > stock}
                  data-testid="book-detail-add"
                  className="h-10 rounded-md bg-primary hover:bg-secondary text-white text-sm font-semibold px-5 flex-1 min-w-[180px] gap-2"
                >
                  <ShoppingBag size={16} /> Add to cart
                </Button>
                <Button
                  onClick={buyNow}
                  disabled={qty > stock}
                  data-testid="book-detail-buy"
                  className="h-10 rounded-md bg-accent hover:bg-accent/90 text-secondary text-sm font-semibold px-5 gap-2"
                >
                  <Bolt size={16} /> Buy now
                </Button>
              </div>
            ) : (
              <div className="mt-3" data-testid="book-detail-oos">
                <Button
                  disabled
                  className="h-10 rounded-md bg-slate-200 text-slate-500 text-sm font-semibold px-5 w-full sm:w-auto cursor-not-allowed"
                  aria-disabled="true"
                >
                  Out of stock
                </Button>
                <p className="mt-1.5 text-[13px] text-slate-600">
                  This title is currently unavailable. We restock most items within 5–10 business days.
                </p>
                <Button
                  onClick={() => toast.success("We'll email you the moment it's back.")}
                  variant="outline"
                  data-testid="book-detail-notify"
                  className="mt-2 h-9 rounded-md border-primary text-primary hover:bg-primary hover:text-white text-sm font-semibold px-5"
                >
                  Notify me when available
                </Button>
              </div>
            )}

            <div className="mt-2 flex gap-4">
              <button onClick={() => { setWishlisted(v => !v); toast.success(wishlisted ? "Removed from wishlist" : "Added to wishlist"); }} className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-primary" data-testid="book-detail-wishlist">
                <Heart size={14} fill={wishlisted ? "currentColor" : "none"} className={wishlisted ? "text-red-500" : ""} /> {wishlisted ? "Wishlisted" : "Add to wishlist"}
              </button>
              <button onClick={shareLink} className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-primary" data-testid="book-detail-share">
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px] text-slate-700" data-testid="trust-badges">
            <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"><ShieldCheck className="text-primary shrink-0" size={16} /><div><strong className="block leading-tight">Secure</strong><span className="text-slate-500">SSL checkout</span></div></div>
            <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"><Truck className="text-primary shrink-0" size={16} /><div><strong className="block leading-tight">Fast</strong><span className="text-slate-500">U.S. shipping</span></div></div>
            {(() => {
              // "Not Returnable" wins when the Ingram feed explicitly flags
              // the title as non-returnable (Returnable Indicator = "N").
              // Anything else — "Y", blank, or missing — keeps the standard
              // Easy 15-day returns badge.
              const ri = String(book.returnable_indicator || "").toUpperCase();
              const notReturnable = ri === "N" || book.is_returnable === false;
              return notReturnable ? (
                <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200" data-testid="trust-badge-returns"><RotateCcw className="text-slate-400 shrink-0" size={16} /><div><strong className="block leading-tight text-slate-500">Not</strong><span className="text-slate-500">Returnable</span></div></div>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200" data-testid="trust-badge-returns"><RotateCcw className="text-primary shrink-0" size={16} /><div><strong className="block leading-tight">Easy</strong><span className="text-slate-500">15-day returns</span></div></div>
              );
            })()}
            <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"><BadgeCheck className="text-primary shrink-0" size={16} /><div><strong className="block leading-tight">Authentic</strong><span className="text-slate-500">Verified genuine</span></div></div>
          </div>

          {/* Description */}
          {book.description && (
            <div className="mt-5">
              <h2 className="font-heading font-semibold text-base text-slate-900 mb-2">About this book</h2>
              <p className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-line">{book.description}</p>
            </div>
          )}

          {/* Details table */}
          {(() => {
            const rows = [
              { label: "Author", value: book.author },
              { label: "ISBN", value: book.isbn },
              { label: "Edition / Year", value: book.year },
              { label: "Publisher", value: book.publisher },
              { label: "Language", value: book.language },
              { label: "Pages", value: book.pages },
              { label: "Dimensions", value: book.dimensions },
              { label: "Weight", value: book.weight },
              { label: "Format", value: Array.isArray(book.formats) ? book.formats.join(", ") : book.formats },
              { label: "Condition", value: book.condition },
              { label: "SKU", value: book.sku },
              { label: "Category", value: book.category },
            ].filter((r) => r.value != null && r.value !== "");
            if (!rows.length) return null;
            return (
              <div className="mt-5" data-testid="book-detail-info-grid">
                <h2 className="font-heading font-semibold text-base text-slate-900 mb-2">Product details</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[13px]">
                  {rows.map((r) => (
                    <div key={r.label} className="flex justify-between border-b border-slate-100 py-1.5" data-testid={`book-detail-info-${r.label.toLowerCase()}`}>
                      <dt className="text-slate-500">{r.label}</dt>
                      <dd className="text-slate-900 font-medium text-right ml-4">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })()}

          {/* Customer reviews */}
          <ReviewsSection book={book} />
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <section className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-primary font-semibold text-xs mb-0.5">You might also like</p>
              <h2 className="font-heading font-bold text-xl text-slate-900">Related books</h2>
            </div>
            <Link to={`/shop?category=${encodeURIComponent(book.category || "")}`} className="text-primary text-sm font-medium">Browse category →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {related.map((b, i) => <ProductCard key={b.id} book={b} index={i} />)}
          </div>
        </section>
      )}

      {/* Sticky mobile buy bar */}
      {inStock && (
        <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-40 flex gap-2" data-testid="mobile-buy-bar">
          <div className="flex-1 flex items-center px-3 rounded-lg bg-slate-50">
            <span className="font-heading font-bold text-lg text-slate-900">${price.toFixed(2)}</span>
          </div>
          <Button onClick={() => doAdd(true)} className="rounded-lg bg-primary hover:bg-secondary text-white h-11 px-5 font-semibold flex-1"><ShoppingBag size={16} className="mr-1" />Add</Button>
          <Button onClick={buyNow} className="rounded-lg bg-accent text-secondary h-11 px-5 font-semibold"><Bolt size={16} className="mr-1" />Buy</Button>
        </div>
      )}
    </div>
  );
}
